/**
 * The book store: one query, one record map, and the invalidation rules.
 *
 * This is the part plan D3 called "a state problem, not a rendering problem" —
 * *"One book record, two mounts, a list behind it repainting."* The mock did it
 * with a global `book:changed` event and a manual re-render; here it is a
 * context, and every surface reads the same record.
 *
 * **Why hand-rolled and not TanStack Query.** The surface is genuinely small:
 * one paginated list query, one record map, three mutations. A query library
 * would earn its place on caching across many endpoints and on background
 * refetch; here it would mostly be API surface. The one thing it would give us
 * for free — not applying a response that arrived after its query changed — is
 * a request-id guard, which is written out below and tested. Revisit when a
 * second screen needs its own cache, not before.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  addCopy as apiAddCopy,
  ApiError,
  createBook as apiCreate,
  deleteBook as apiDelete,
  lendCopy as apiLendCopy,
  listBooks,
  patchBook as apiPatch,
  patchCopy as apiPatchCopy,
  returnCopy as apiReturnCopy,
  type Book,
  type CopyCreate,
  type CopyPatch,
  type LendRequest,
} from '../api/client'

export type SortKey = 'title' | 'author' | 'recently_added'
export type StatusFilter = 'auto' | 'approved' | 'manual' | null

export interface BooksQuery {
  q: string
  sort: SortKey
  ascending: boolean
  status: StatusFilter
  authorKey: string | null
  /** "Who has my books" (§5.2) — books with at least one copy out. */
  lentOut: boolean
  /** The "duplicates to resolve" queue (§5.4, P2.6) — books with at least
   *  one still-open copy-resolution question. Same boolean-filter shape as
   *  lentOut; the server does the join against the DuplicateQueue. */
  duplicates: boolean
}

/**
 * The direction a sort key means when you pick it. A-Z for text, newest-first
 * for a date — "recently added, oldest first" is a thing nobody chooses, and
 * it is what you would get if direction simply survived a key change.
 */
export function naturalAscending(sort: SortKey): boolean {
  return sort !== 'recently_added'
}

export const EMPTY_QUERY: BooksQuery = {
  q: '',
  sort: 'title',
  ascending: true,
  status: null,
  authorKey: null,
  lentOut: false,
  duplicates: false,
}

/** 30 matches the mock's feel; the API caps a page at 200. */
export const PAGE_SIZE = 30

interface BooksState {
  query: BooksQuery
  items: Book[]
  total: number
  loading: boolean
  /** A page beyond the first is in flight — distinct from `loading`, so the
   *  feed can keep showing rows instead of blanking on every scroll. */
  loadingMore: boolean
  error: string | null
  hasMore: boolean
}

export interface BooksApi extends BooksState {
  setQuery: (patch: Partial<BooksQuery>) => void
  resetQuery: () => void
  loadMore: () => void
  /** Refetch the current query from the top — see the implementation's ⚠. */
  reload: () => void
  /** Sorting is ignored by the server while searching — relevance IS the
   *  order — so the UI must say so rather than pretend the control applies. */
  sortApplies: boolean
  filtersActive: boolean
  get: (id: string) => Book | undefined
  edit: (id: string, patch: { title?: string; author?: string }) => Promise<Book>
  add: (payload: { title: string; author: string }) => Promise<Book>
  remove: (id: string) => Promise<void>
  addCopy: (bookId: string, payload: CopyCreate) => Promise<Book>
  patchCopy: (bookId: string, copyId: string, payload: CopyPatch) => Promise<Book>
  lendCopy: (bookId: string, copyId: string, payload: LendRequest) => Promise<Book>
  returnCopy: (bookId: string, copyId: string) => Promise<Book>
}

const Ctx = createContext<BooksApi | null>(null)

export function useBooks(): BooksApi {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useBooks outside <BooksProvider>')
  return ctx
}

function toParams(query: BooksQuery, offset: number) {
  const searching = query.q.trim().length > 0
  return {
    limit: PAGE_SIZE,
    offset,
    ...(searching ? { q: query.q.trim() } : {}),
    // Sending a sort alongside q would be a lie; the server ignores it.
    ...(searching ? {} : { sort: query.sort, ascending: query.ascending }),
    ...(query.status ? { status: query.status } : {}),
    ...(query.authorKey ? { author_key: query.authorKey } : {}),
    ...(query.lentOut ? { lent_out: true } : {}),
    ...(query.duplicates ? { duplicates: true } : {}),
  }
}

export function BooksProvider({ children }: { children: ReactNode }) {
  const [query, setQueryState] = useState<BooksQuery>(EMPTY_QUERY)
  const [state, setState] = useState<Omit<BooksState, 'query'>>({
    items: [],
    total: 0,
    loading: true,
    loadingMore: false,
    error: null,
    hasMore: false,
  })

  // Every fetch carries the id of the query it belongs to. A response whose id
  // is stale is DROPPED — otherwise a slow page-3 request lands after the user
  // has typed a search and appends books that do not match it. This is the one
  // race a query library would have handled for us.
  const queryId = useRef(0)
  const inFlight = useRef<AbortController | null>(null)

  const run = useCallback(
    async (q: BooksQuery, offset: number, id: number) => {
      inFlight.current?.abort()
      const controller = new AbortController()
      inFlight.current = controller
      setState((s) => ({
        ...s,
        error: null,
        loading: offset === 0,
        loadingMore: offset > 0,
      }))
      try {
        const page = await listBooks({
          query: toParams(q, offset),
          signal: controller.signal,
        })
        if (id !== queryId.current) return // superseded; drop it
        setState((s) => {
          const items = offset === 0 ? page.items : [...s.items, ...page.items]
          return {
            items,
            total: page.total,
            loading: false,
            loadingMore: false,
            error: null,
            hasMore: items.length < page.total,
          }
        })
      } catch (err) {
        if (controller.signal.aborted || id !== queryId.current) return
        setState((s) => ({
          ...s,
          loading: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : String(err),
        }))
      }
    },
    [],
  )

  // Refetch from the top whenever the query changes. `query` is a value
  // object, so this fires on exactly the changes that matter.
  useEffect(() => {
    const id = ++queryId.current
    void run(query, 0, id)
    return () => inFlight.current?.abort()
  }, [query, run])

  const setQuery = useCallback((patch: Partial<BooksQuery>) => {
    setQueryState((prev) => {
      // Changing the KEY resets the direction to that key's natural one,
      // unless the caller asked for a direction in the same breath. Carrying
      // "ascending" from title onto recently_added would silently answer a
      // question nobody asked (oldest books first).
      const reset =
        patch.sort !== undefined &&
        patch.sort !== prev.sort &&
        patch.ascending === undefined
      const next = {
        ...prev,
        ...patch,
        ...(reset ? { ascending: naturalAscending(patch.sort as SortKey) } : {}),
      }
      // Same values in, same object out: avoids a refetch when a debounced
      // search box re-emits the text the user already typed.
      const same = (Object.keys(next) as (keyof BooksQuery)[]).every(
        (k) => next[k] === prev[k],
      )
      return same ? prev : next
    })
  }, [])

  const resetQuery = useCallback(() => setQueryState(EMPTY_QUERY), [])

  /**
   * Refetch the first page of the CURRENT query, keeping the query itself.
   *
   * ⚠ This exists because the list is not the only thing that writes books.
   * Approving a finding, adding one by hand, splitting a spine into volumes —
   * all happen on the Capture tab, through routes this store never sees, and
   * the store's only other refetch trigger is a change of `query`. So
   * returning to the Books tab showed a list from before that work, and
   * typing anything in the search box "fixed" it — which is exactly how the
   * owner found it: *"after searching, the books appeared"*.
   *
   * A cross-tab invalidation channel (an event bus, a query library) is the
   * general answer and is not worth it for two tabs: the moment that matters
   * is ENTERING the tab, and `BooksTab` unmounts when you leave it, so its
   * own mount is the signal. Revisit if a third writer appears that does not
   * come with a mount.
   */
  const reload = useCallback(() => {
    const id = ++queryId.current
    void run(query, 0, id)
  }, [query, run])

  const loadMore = useCallback(() => {
    setState((s) => {
      if (s.loading || s.loadingMore || !s.hasMore) return s
      void run(query, s.items.length, queryId.current)
      return { ...s, loadingMore: true }
    })
  }, [query, run])

  /** Replace one record in place. This is "edit it anywhere, it changes
   *  everywhere": the drawer, the full page and the row behind them all read
   *  from this list, so one write repaints all three. */
  const replace = useCallback((book: Book) => {
    setState((s) => ({
      ...s,
      items: s.items.map((b) => (b.id === book.id ? book : b)),
    }))
  }, [])

  const edit = useCallback(
    async (id: string, patch: { title?: string; author?: string }) => {
      const updated = await apiPatch(id, patch)
      replace(updated)
      return updated
    },
    [replace],
  )

  const remove = useCallback(async (id: string) => {
    await apiDelete(id)
    setState((s) => ({
      ...s,
      items: s.items.filter((b) => b.id !== id),
      total: Math.max(0, s.total - 1),
    }))
  }, [])

  const add = useCallback(
    async (payload: { title: string; author: string }) => {
      const created = await apiCreate(payload)
      // A new book usually belongs on a page the user has not loaded, so
      // splicing it into `items` would put it in the wrong place. Refetching
      // the pages already loaded is correct AND keeps the scroll position —
      // the alternative, jumping back to page 1, loses the reader's place.
      const id = ++queryId.current
      setState((s) => {
        void run(query, 0, id)
        return s
      })
      return created
    },
    [query, run],
  )

  // Every copy mutation returns the whole updated Book (server-side, so the
  // client never reassembles one from a partial response) and goes through
  // the same `replace()` the book-level `edit` uses — one book record, every
  // surface showing it repaints.
  const addCopy = useCallback(
    async (bookId: string, payload: CopyCreate) => {
      const updated = await apiAddCopy(bookId, payload)
      replace(updated)
      return updated
    },
    [replace],
  )
  const patchCopy = useCallback(
    async (bookId: string, copyId: string, payload: CopyPatch) => {
      const updated = await apiPatchCopy(bookId, copyId, payload)
      replace(updated)
      return updated
    },
    [replace],
  )
  const lendCopy = useCallback(
    async (bookId: string, copyId: string, payload: LendRequest) => {
      const updated = await apiLendCopy(bookId, copyId, payload)
      replace(updated)
      return updated
    },
    [replace],
  )
  const returnCopy = useCallback(
    async (bookId: string, copyId: string) => {
      const updated = await apiReturnCopy(bookId, copyId)
      replace(updated)
      return updated
    },
    [replace],
  )

  const get = useCallback(
    (id: string) => state.items.find((b) => b.id === id),
    [state.items],
  )

  const value = useMemo<BooksApi>(
    () => ({
      ...state,
      query,
      setQuery,
      resetQuery,
      loadMore,
      reload,
      sortApplies: query.q.trim().length === 0,
      filtersActive:
        query.q.trim() !== '' ||
        query.status !== null ||
        query.authorKey !== null ||
        query.lentOut ||
        query.duplicates ||
        query.sort !== EMPTY_QUERY.sort ||
        query.ascending !== naturalAscending(query.sort),
      get,
      edit,
      add,
      remove,
      addCopy,
      patchCopy,
      lendCopy,
      returnCopy,
    }),
    [state, query, setQuery, resetQuery, loadMore, reload, get, edit, add, remove,
     addCopy, patchCopy, lendCopy, returnCopy],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** 409 means "you already own that book" — a normal answer, not a failure. */
export function isConflict(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409
}
